import type { GroupedSummary } from '../healthWalletConstants';
import type { TrendGroup, EncounterVisit } from '../healthWalletSlice';

// ============================================================================
// Current/Past classification — the ONE shared brain for the Conditions tab
// (hero tiles, filter pills, section buckets, card chips all call this, so
// they can never disagree). Rules (Leo, 2026-07-12):
//   PRIMARY:  the group's dominant clinicalStatus (get_patient_resource
//             `status` field): active/recurrence/relapse → current;
//             resolved/inactive/remission → past.
//   FALLBACK: status-less groups (e.g. Epic's conditions, which carry no
//             clinicalStatus) classify by RECENCY — most recent documentation
//             within the past 12 months → current, else past.
// NB: stored statuses are truthful only after a re-sync through the fixed
// converter (Problem Status fold) — stale data reads all-active (expected).
// ============================================================================

export type CurrentPast = 'current' | 'past';
export type ConditionBucket = CurrentPast | 'symptom';
export type MedBucket = CurrentPast | 'infusion' | 'record';

// Per-domain status vocabularies — closed FHIR value-sets, kept as ONE code map on purpose.
// DEFERRED DECISION (Leo 2026-07-13): promote to a status_buckets DB table + SQL-side
// classification only when bucketing becomes POLICY (per-client rules / runtime tuning).
const STATUS_BUCKETS: Record<string, { current: Set<string>; past: Set<string> }> = {
  conditions: {
    current: new Set(['active', 'recurrence', 'relapse']),
    past: new Set(['resolved', 'inactive', 'remission']),
  },
  medications: {
    current: new Set(['active', 'intended', 'on-hold']),
    past: new Set(['completed', 'stopped', 'cancelled', 'not-taken', 'entered-in-error']),
  },
};
const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;

// Status primary (per-domain vocab), 12-month recency fallback for status-less/unknown (Leo).
function classifyByStatus(domain: string, g: GroupedSummary, now: number): CurrentPast {
  const vocab = STATUS_BUCKETS[domain];
  const s = (g.status ?? '').toLowerCase();
  if (vocab?.current.has(s)) return 'current';
  if (vocab?.past.has(s)) return 'past';
  const last = g.lastDate ? Date.parse(g.lastDate) : NaN;
  return !Number.isNaN(last) && now - last <= TWELVE_MONTHS_MS ? 'current' : 'past';
}

export function classifyGroup(g: GroupedSummary, now: number = Date.now()): CurrentPast {
  return classifyByStatus('conditions', g, now);
}

// Bucket incl. the Symptoms section (2026-07-13): symptom-kind groups (SNOMED semantic tag,
// from get_patient_resource `kind`) get their OWN section per the design — they leave
// Current/Past entirely. Unclassified kinds stay in Current/Past (we don't fake buckets).
// Administrative bucket DEFERRED (Leo).
export function bucketGroup(g: GroupedSummary, now: number = Date.now()): ConditionBucket {
  if (g.kind === 'symptom') return 'symptom';
  return classifyGroup(g, now);
}

// Infusions & IV therapy detection — CODES-primary (Leo 2026-07-13: display spellings vary by
// org and we can't code to one patient's strings; e.g. BCAN's intravesical BCG never appears in
// Alan's MM data). Parenteral / clinic-administered route CODES:
//   SNOMED route-of-administration: 47625008 IV, 34206005 subcutaneous, 78421000 intramuscular,
//   72607000 intrathecal, 404820008 epidural, 372464004 intravesical (BCAN's BCG instillations).
//   HL7/NCI (CCDA routeCode, OID 2.16.840.1.113883.5.112): C38276 IV, C38299 subcutaneous,
//   C28161 intramuscular, C38267 intrathecal, C38243 epidural, C38131 intravesical.
const INFUSION_ROUTE_CODES = new Set([
  '47625008', '34206005', '78421000', '72607000', '404820008', '372464004',
  'C38276', 'C38299', 'C28161', 'C38267', 'C38243', 'C38131',
]);
// Display-text signal — REQUIRED even when a code exists: Epic codes routes with PROPRIETARY
// per-org integers (system urn:oid:….798268.7025, code "193" = IV Piggyback), so an unrecognized
// code must still fall through to text (probe 2026-07-13; gating text on "uncoded" made Infusions 0).
// For Epic the display comes from the same Epic-standard category list, so text ≈ as stable as the
// integers; the standard-code set above serves CCDA + standards-compliant sources.
// Terms from Alan's real distribution + intraves for BCG; \biv\b catches the "IV …" family.
const INFUSION_ROUTE_TEXT = /intraven|subcutan|inject|infus|epidural|intramusc|intrathec|intracath|intraves|piggyback|\biv\b/i;

// Route-named ADMIN records (Leo 2026-07-13, Option 1 + design): administration entries whose
// source has NO medication name leak the route label as their group name ("IV Push" ×47). They're
// real treatments, not medications-you-can-name → quarantined into "Other treatment records".
// Detection: group name IS its own route, or one of the route/admin-category labels (Epic's stable
// category vocabulary + the design's admin list — NOT patient-specific strings). Self-shrinking:
// the converter's name-recovery fallback renames rows on future re-converts and they leave here.
const ROUTE_RECORD_NAMES = new Set([
  'iv infusion', 'iv piggyback', 'iv push', 'subcutaneous', 'oral', 'injection', 'status',
  'home infusion', 'intramuscular', 'intracatheter', 'infuser', 'continuous infusion',
  'intravenous', 'miscellaneous', 'supply item', 'inhalation', 'nasal', 'rectal',
  'intrathecal', 'epidural',
]);
// Provider roles / personnel labels that leaked into the medication-name field (source data quality —
// e.g. a MedicationStatement coded from an HL7 role table, "REGISTERED NURSE …"). Quarantine to the
// muted "Other treatment records" bucket rather than showing them as active meds. NOT deleted — they
// stay visible there with their history. Keyword-based (administrative terms), so disease-general.
const NON_MED_ROLE_RE = /\b(nurse|physician|anesthesiolog|radiolog|cardiolog|oncolog|surgeon|patholog|technolog|therapist|practitioner|provider|assistant)/i;
const isRouteRecord = (g: GroupedSummary): boolean => {
  const n = (g.name ?? '').trim().toLowerCase();
  if (!n) return false;
  return n === (g.route ?? '').trim().toLowerCase() || ROUTE_RECORD_NAMES.has(n) || NON_MED_ROLE_RE.test(n);
};

export function bucketMedGroup(g: GroupedSummary, now: number = Date.now()): MedBucket {
  if (isRouteRecord(g)) return 'record';
  if (g.routeCode && INFUSION_ROUTE_CODES.has(g.routeCode)) return 'infusion';
  if (g.route && INFUSION_ROUTE_TEXT.test(g.route)) return 'infusion';
  return classifyByStatus('medications', g, now);
}

// ---------------------------------------------------------------------------
// Encounter visit-type classification (design's 9 chips) — TWO-SIGNAL like med
// routes: type-display keywords FIRST (most specific — Epic/CCDA displays are
// stable category names; probe 2026-07-13), then FHIR class codes (Epic ALSO
// puts proprietary integers and CPT E/M codes in class, so unknowns fall to
// keyword/default). Order matters: surgery before hospital ("Hospital
// Outpatient Surgery"), specific before the broad office bucket.
// ---------------------------------------------------------------------------
export type EncounterTypeKey =
  | 'office' | 'hospital' | 'ed' | 'urgent' | 'infusion' | 'surgery' | 'imaging' | 'lab' | 'telehealth' | 'visit';

export const ENCOUNTER_TYPE_META: Record<EncounterTypeKey, { label: string; bg: string; fg: string }> = {
  office:     { label: 'Office visit',  bg: '#eef3fb', fg: '#2f5fb0' },
  hospital:   { label: 'Hospital stay', bg: '#eceef5', fg: '#3f4a6b' },
  ed:         { label: 'Emergency',     bg: '#fbeef0', fg: '#a44a58' },
  urgent:     { label: 'Urgent care',   bg: '#fdf2e6', fg: '#8a6a3a' },
  infusion:   { label: 'Infusion visit', bg: '#efeaff', fg: '#6C4CE0' },
  surgery:    { label: 'Surgery',       bg: '#f3eee6', fg: '#8a6a3a' },
  imaging:    { label: 'Imaging',       bg: '#eef2f2', fg: '#4a6b6b' },
  lab:        { label: 'Laboratory',    bg: '#e6f6ec', fg: '#1a7f3c' }, // brightened (Leo) — matches the Trends "steady" green
  telehealth: { label: 'Telehealth',    bg: '#f0eef7', fg: '#5a4a8a' },
  visit:      { label: 'Visit',         bg: '#f0f0f3', fg: '#6b6b70' },
};
// Canonical order — hero tiles + filter pills iterate this so both present-only lists stay in sync.
export const ENCOUNTER_TYPE_ORDER: EncounterTypeKey[] =
  ['office', 'hospital', 'ed', 'urgent', 'infusion', 'surgery', 'imaging', 'lab', 'telehealth', 'visit'];

const ENCOUNTER_TEXT_RULES: Array<[RegExp, EncounterTypeKey]> = [
  [/surg/i, 'surgery'],
  [/radiol|imaging/i, 'imaging'],
  [/urgent/i, 'urgent'],
  [/emergen/i, 'ed'],
  [/infus/i, 'infusion'],
  [/virtual|telehealth|telephone|video/i, 'telehealth'],
  [/inpatient|hospital encounter|acute care|discharge/i, 'hospital'],
  [/\blab\b|laboratory|specimen|clinical support/i, 'lab'],
  [/physician|medicine|practitioner|nurse|clinic|office|outpatient|ambulatory/i, 'office'],
];

export function encounterTypeKey(v: EncounterVisit): EncounterTypeKey {
  const t = v.type ?? '';
  for (const [re, key] of ENCOUNTER_TEXT_RULES) if (re.test(t)) return key;
  const c = (v.classCode ?? '').toUpperCase();
  if (c === 'IMP' || c === 'ACUTE') return 'hospital';
  if (c === 'EMER') return 'ed';
  if (c === 'VR' || c === '99443') return 'telehealth'; // 99443 = telephone E/M
  if (c === 'AMB' || /^99\d{3}$/.test(c)) return 'office'; // CPT E/M codes in class = office visits
  return 'visit';
}

export function encounterTypeMeta(v: EncounterVisit): { key: EncounterTypeKey; label: string; bg: string; fg: string } {
  const key = encounterTypeKey(v);
  return { key, ...ENCOUNTER_TYPE_META[key] };
}

// ---------------------------------------------------------------------------
// Planned / upcoming classification (Leo 2026-07-22). The backend now flags
// scheduled/future appointments on encounters (`isUpcoming`) and carries the
// raw CDA moodCode (`mood`) + FHIR request intent (`intent`) on medication/
// procedure groups. Encounters drive the Upcoming tab TODAY; the meds/procedure
// helpers below are SCAFFOLDING for a later step (badge + routing) once the
// converter emits mood/intent — they are intentionally NOT wired to any UI yet.
// ---------------------------------------------------------------------------

// The ONLY upcoming signal we trust for visits: the backend's explicit flag.
export function isVisitUpcoming(v: EncounterVisit): boolean {
  return v.isUpcoming === true;
}

const MOOD_LABEL: Record<string, string> = { INT: 'planned', RQO: 'ordered', PRP: 'proposed', PRMS: 'promised' };
const INTENT_LABEL: Record<string, string> = {
  proposal: 'proposed', plan: 'planned', order: 'ordered', 'original-order': 'ordered',
  'instance-order': 'ordered', 'reflex-order': 'ordered', 'filler-order': 'ordered',
  directive: 'planned', option: 'proposed',
};

// Badge text for a planned/ordered resource: FHIR intent PRIMARY, CDA moodCode FALLBACK, else null.
export function moodBadgeLabel(g: GroupedSummary): string | null {
  return INTENT_LABEL[g.intent ?? ''] ?? MOOD_LABEL[g.mood ?? ''] ?? null;
}

// Whether a medication/procedure group is a FUTURE/planned item to surface in the Upcoming tab.
// Meds: only a proposal/plan intent counts (an order or absence → false; an active med list is
// moodCode=INT and would falsely route, so mood is NOT consulted for meds).
// Procedures: a planned/ordered request (FHIR intent, or a non-EVN mood fallback) that is scheduled for the
// FUTURE. "Upcoming" = not-yet-happened → it MUST be future-dated (mirrors the encounter isUpcoming date rule).
// The date gate is LOAD-BEARING: undated/past orders — e.g. Alan's ~72 standing lab/imaging/education orders,
// all moodCode=INT with NO date — are NOT upcoming; without it they'd flood Upcoming via the mood fallback.
// See project_hw_upcoming_resource_classification (Stage 3).
export function isResourceUpcoming(g: GroupedSummary, domain: 'medications' | 'procedures', now: number = Date.now()): boolean {
  if (domain === 'medications') return g.intent === 'proposal' || g.intent === 'plan';
  if (!isPlannedOrder(g)) return false;
  const when = g.lastDate ? Date.parse(g.lastDate) : NaN;
  return !Number.isNaN(when) && when > now;
}

// Base "planned/ordered request" signal for a PROCEDURE group — a ServiceRequest (FHIR intent) or a non-EVN
// moodCode, DATE-AGNOSTIC. Split by date: FUTURE → Upcoming (isResourceUpcoming); undated/past → the Procedures
// tab's "Orders" section (ordered/planned but not upcoming, and not a performed procedure either). Everything
// else (no intent, EVN/no mood) = a performed procedure → the normal category sections.
export function isPlannedOrder(g: GroupedSummary): boolean {
  return !!g.intent || (!!g.mood && g.mood !== 'EVN');
}

// ---------------------------------------------------------------------------
// Procedure categories (V1 Procedures tab) — TWO-SIGNAL: CPT/HCPCS code ranges
// primary (labs 8xxxx + venipuncture 36415, imaging 7xxxx, drug-administration
// 96360-96549, J-code drugs, bone-marrow 3822x/3823x), name keywords fallback.
// NO separate Infusions category (Leo 2026-07-13, vs the design): administration
// codes merge into Treatments — the design itself split 96401 (Treatments) from
// 96365 (Infusions), and "Infusion" already means the drug on Medications and
// the visit on Encounters. Biopsy checked FIRST (85097 sits in the lab range).
// ---------------------------------------------------------------------------
export type ProcCategoryKey = 'treatment' | 'lab' | 'imaging' | 'biopsy' | 'education' | 'other';

// Single source for the tab's categories (hero tiles + pills + sections all read it): `label` = card chip
// (singular), `title` = section header, `pill` = filter + hero tile (short), `dot` = section dot, `bg/fg` = tile.
export const PROC_CATEGORY_META: Record<ProcCategoryKey, { label: string; title: string; pill: string; dot: string; bg: string; fg: string }> = {
  treatment: { label: 'Treatment',        title: 'Treatments',         pill: 'Treatments', dot: '#6C4CE0', bg: '#efeaff', fg: '#6C4CE0' },
  lab:       { label: 'Laboratory test',  title: 'Laboratory tests',   pill: 'Laboratory', dot: '#34a760', bg: '#e6f6ec', fg: '#1a7f3c' },
  imaging:   { label: 'Imaging',          title: 'Imaging',            pill: 'Imaging',    dot: '#4a6b6b', bg: '#eef2f2', fg: '#4a6b6b' },
  biopsy:    { label: 'Biopsy or sample', title: 'Biopsies & samples', pill: 'Biopsies',   dot: '#c08a3a', bg: '#f3eee6', fg: '#8a6a3a' },
  education: { label: 'Education',        title: 'Education',          pill: 'Education',  dot: '#8a7ac0', bg: '#f0eef7', fg: '#5a4a8a' },
  other:     { label: 'Other procedure',  title: 'Other procedures',   pill: 'Other',      dot: '#b0b0b6', bg: '#f0f0f3', fg: '#6b6b70' },
};

export function procCategoryKey(g: GroupedSummary): ProcCategoryKey {
  const code = (g.code ?? '').toUpperCase();
  const name = (g.name ?? '').toLowerCase();
  if (/biops|aspirat|marrow|procurement|sampl/.test(name) || /^382[23]\d$/.test(code)) return 'biopsy';
  if (/educat|teaching|counsel/.test(name)) return 'education';
  if (/^J\d{4}$/.test(code)) return 'treatment'; // HCPCS J-codes = administered drugs
  if (/loinc/i.test(g.codeSystem ?? '')) return 'lab'; // LOINC IS the lab vocabulary (probe: 20 rows)
  const n = /^\d{5}$/.test(code) ? parseInt(code, 10) : NaN;
  if (!Number.isNaN(n)) {
    if (n === 36415 || (n >= 80000 && n <= 89999)) return 'lab';
    if (n >= 70000 && n <= 79999) return 'imaging';
    if (n >= 96360 && n <= 96549) return 'treatment'; // hydration/infusion/injection/chemo administration
  }
  if (/x-ray|xray|\bmri\b|ct scan|\bpet\b|imaging|ultrasound|radiograph|mammogra|fluoroscop/.test(name)) return 'imaging';
  if (/panel|blood count|electrophoresis|venipuncture|assay|titer|culture|screen|\blab\b/.test(name)) return 'lab';
  if (/infus|chemo|immunother|transfus|radiation|injection|administr/.test(name)) return 'treatment';
  return 'other';
}

export function procCategoryMeta(g: GroupedSummary): { key: ProcCategoryKey; label: string; title: string; bg: string; fg: string } {
  const key = procCategoryKey(g);
  return { key, ...PROC_CATEGORY_META[key] };
}

// ---------------------------------------------------------------------------
// Observation sections (V1 Observations tab) — FHIR category primary (Epic
// stamps every Observation), lab-name keyword safety net for uncategorized
// rows (user-upload extractor omits category — Prashanth ask pending).
// 'monitored' ("Checked most often") is an OVERLAY computed in the sections
// component: top-N lab groups by record count, disease-GENERAL by design
// (CEO principle — no disease-specific curation; frequency IS the monitoring
// signal and self-adapts to any patient).
// ---------------------------------------------------------------------------
export type ObsSectionKey = 'monitored' | 'lab' | 'vital' | 'assessment' | 'care';

export function obsBaseSection(g: GroupedSummary): Exclude<ObsSectionKey, 'monitored'> {
  const c = (g.category ?? '').toLowerCase();
  if (c === 'laboratory') return 'lab';
  if (c === 'vital-signs') return 'vital';
  if (c === 'social-history' || c === 'survey' || c === 'sdoh') return 'assessment';
  if (!c && /\bin (blood|serum|plasma|urine)\b|serum or plasma/i.test(g.name ?? '')) return 'lab';
  return 'care'; // smartdata / exam / procedure / unknown — the catch-all
}

// ---------------------------------------------------------------------------
// Allergy sections (V1 Allergies tab) — FHIR AllergyIntolerance.category
// (medication/food/environment) → the design's Medication/Food/Other sections.
// Admin/status entries ("Patient not asked") become the design's FOOTNOTE
// sentence, not allergy cards.
// ---------------------------------------------------------------------------
export type AllergyCategoryKey = 'medication' | 'food' | 'other';

export function allergyCategoryKey(g: GroupedSummary): AllergyCategoryKey {
  const c = (g.category ?? '').toLowerCase();
  if (c === 'medication') return 'medication';
  if (c === 'food') return 'food';
  return 'other'; // environment / biologic / uncategorized
}

const ALLERGY_ADMIN_NAMES = new Set([
  'patient not asked', 'no known allergies', 'no known drug allergies', 'unable to assess',
  'active', 'no longer active', 'status', 'not on file',
]);
export const isAllergyAdminRecord = (g: GroupedSummary): boolean =>
  ALLERGY_ADMIN_NAMES.has((g.name ?? '').trim().toLowerCase());

// Case-insensitive merge KEY for the Allergies tab's card merge (useAllergyGroups).
export const allergyMergeKey = (g: GroupedSummary): string => (g.name ?? '').trim().toLowerCase();

// Card's "Latest" line: numeric value + unit, else the categorical text ("Never smoker").
export function obsLatestLabel(g: GroupedSummary): string | null {
  if (g.latestValue != null) return `${g.latestValue}${g.latestUnit ? ` ${g.latestUnit}` : ''}`;
  return g.latestText || null;
}

// Status chip meta for a card — null = no chip (status-less group; we don't fake data).
export function statusChip(g: GroupedSummary): { label: string; bg: string; fg: string } | null {
  const s = (g.status ?? '').toLowerCase();
  if (!s) return null;
  if (STATUS_BUCKETS.conditions.current.has(s)) return { label: s === 'active' ? 'Active' : s[0].toUpperCase() + s.slice(1), bg: '#eef3fb', fg: '#2f5fb0' };
  if (STATUS_BUCKETS.conditions.past.has(s)) return { label: s === 'resolved' ? 'Resolved' : s[0].toUpperCase() + s.slice(1), bg: '#f0f0f3', fg: '#6b6b70' };
  return { label: s, bg: '#f0f0f3', fg: '#6b6b70' };
}

// ---------------------------------------------------------------------------
// Trends buckets (design: Needs attention / Changed recently / Steady / Data
// only). ALL deterministic — no AI: latest reading vs the group's dominant
// reference range, plus a simple last-vs-previous delta for "changed".
// CHANGED_PCT is the recorded tunable (start simple, tune on real data — Leo).
// ---------------------------------------------------------------------------
export type TrendBucket = 'attention' | 'changed' | 'steady' | 'historical' | 'dataonly';
const CHANGED_PCT = 0.1;
// Recency gate (Leo 2026-07-13: "historical flagged"): a decade-old out-of-range manual diff
// shouldn't scream "Needs attention" — out-of-range groups whose LATEST reading is older than
// this go to the muted Historical bucket; stale in-range/changed groups just read as steady.
const HISTORICAL_MS = 24 * 30.44 * 24 * 60 * 60 * 1000; // ~24 months

const lastVals = (t: TrendGroup): { cur: number | null; prev: number | null } => {
  const vals = (t.data ?? []).map((p) => p.value).filter((v) => typeof v === 'number' && !Number.isNaN(v));
  return { cur: vals.length ? vals[vals.length - 1] : null, prev: vals.length > 1 ? vals[vals.length - 2] : null };
};

const isStale = (t: TrendGroup, now: number): boolean => {
  const last = t.lastDate ? Date.parse(t.lastDate) : NaN;
  return !Number.isNaN(last) && now - last > HISTORICAL_MS;
};

export function bucketTrendGroup(t: TrendGroup, now: number = Date.now()): TrendBucket {
  const { cur, prev } = lastVals(t);
  const hasRef = t.refLow != null || t.refHigh != null;
  if (!hasRef || cur == null) return 'dataonly';
  const out = (t.refHigh != null && cur > t.refHigh) || (t.refLow != null && cur < t.refLow);
  if (out) return isStale(t, now) ? 'historical' : 'attention';
  if (!isStale(t, now) && prev != null && Math.abs(cur - prev) > CHANGED_PCT * (Math.abs(prev) || Math.abs(cur) || 1)) return 'changed';
  return 'steady';
}

// Badge + spark color meta per bucket; label reads the actual range position (design badgeText).
export function trendBadge(t: TrendGroup): { label: string; bg: string; fg: string; spark: string } {
  const { cur } = lastVals(t);
  const bucket = bucketTrendGroup(t);
  if (bucket === 'dataonly') return { label: 'Not rated as high/low', bg: '#eeeef1', fg: '#6b6b70', spark: '#b0b0b6' };
  const pos = cur != null && t.refHigh != null && cur > t.refHigh ? 'Above range'
            : cur != null && t.refLow != null && cur < t.refLow ? 'Below range' : 'In range';
  // Muted red (Leo): historical out-of-range should read "was a finding, not an alarm" — not grey.
  if (bucket === 'historical') return { label: `${pos} · older result`, bg: '#f6e9e7', fg: '#9c4a42', spark: '#c0736b' };
  if (bucket === 'attention') return { label: pos, bg: '#fbe6e3', fg: '#bd352b', spark: '#c0362c' };
  // In-range buckets differ only by movement — say so in the label (the color alone read as an unexplained
  // "why is In range amber?"). pos is always "In range" here (out-of-range → attention/historical above).
  if (bucket === 'changed') return { label: `${pos} · moved notably`, bg: '#fdf0dd', fg: '#9a5b00', spark: '#c98416' };
  return { label: `${pos} · holding`, bg: '#e6f6ec', fg: '#1a7f3c', spark: '#34a760' };
}

// Deterministic "why" one-liner (template, NOT AI — the design's per-card explainer slot).
export function trendWhy(t: TrendGroup): string {
  const { cur, prev } = lastVals(t);
  const bucket = bucketTrendGroup(t);
  const dir = cur != null && prev != null ? (cur > prev ? 'risen' : cur < prev ? 'fallen' : 'held steady') : null;
  if (bucket === 'dataonly') return 'This measure has no single normal range, so we show your readings without a high or low label.';
  if (bucket === 'historical') {
    const pos = cur != null && t.refHigh != null && cur > t.refHigh ? 'above' : 'below';
    const yr = t.lastDate ? new Date(t.lastDate).getFullYear() : null;
    return `Was ${pos} the normal range, but the most recent reading is from ${yr ?? 'some time ago'} — an older result your care team has likely already reviewed.`;
  }
  if (bucket === 'attention') {
    const pos = cur != null && t.refHigh != null && cur > t.refHigh ? 'Above' : 'Below';
    return `${pos} the normal range${dir && dir !== 'held steady' ? ` and has ${dir} since the previous reading` : ''}.`;
  }
  if (bucket === 'changed') return `Within the normal range, but has ${dir ?? 'moved'} notably since the previous reading.`;
  return 'Within the normal range and holding steady.';
}

// Medication chip — bucket-driven labels per the design (Active / No longer active / Infusion · IV).
export function medStatusChip(g: GroupedSummary, now: number = Date.now()): { label: string; bg: string; fg: string } | null {
  const bucket = bucketMedGroup(g, now);
  if (bucket === 'record') return { label: 'Name not recorded', bg: '#f0f0f3', fg: '#6b6b70' };
  if (bucket === 'infusion') return { label: 'Infusion / IV', bg: '#efeaff', fg: '#6C4CE0' };
  if (!g.status) return null; // recency-classified only — no chip, we don't fake a status
  return bucket === 'current'
    ? { label: 'Active', bg: '#eef3fb', fg: '#2f5fb0' }
    : { label: 'No longer active', bg: '#f0f0f3', fg: '#6b6b70' };
}
